import locales from '@/locales/locales.js'
import store from '@/store/store.js'
import { OrganizationPath } from '@/router/organizationPaths'

const BASE_URL = process.env.VUE_APP_URL;

/**
 * URL utility functions
 */
export default {
  
  prepareQuery (queryMap, hasParametersInUrl) {
    var query = ''
    for (var [key, value] of queryMap.entries()) {
      if (!query && !hasParametersInUrl) {
        query += '?'
      } else {
        query += '&'
      }

      if (value === 0) {
        value = '0'
      } else if (!value) {
        value = ''
      }

      query += key + '=' + this.encodeParam(value)
    }
    return query
  },

  addQueryParams (url, queryMap) {
    let hasParametersInUrl = url.indexOf('?') > 0;
    var query = this.prepareQuery(queryMap, hasParametersInUrl)
    return url + query
  },

  encodeParam (value) {
    if (!value) {
      return ''
    }
    return encodeURIComponent(value)
  },

  getFullUrl(path){
    if (!path){
      path = ''
    }

    if (path.startsWith('/')){
      path = path.substring(1)
    }

    if (!path.startsWith('http')){
      let baseUrl = BASE_URL
      if (baseUrl.endsWith('/')){
        baseUrl = baseUrl.substring(0, baseUrl.length - 1)
      }
      path = baseUrl + '/' + path
    }

    return path
  },
  
  getLocalisedUrl (url) {
    if (!url) {
      return null
    }

    const locale = locales.getLocale()
    if (url.startsWith(locale.base + '/') || url === locale.base) {
      return url
    }

    if (url.startsWith('/')) {
      url = url.substring(1)
    }

    return locale.base + '/' + url
  },
  getLocalisedMyPagesUrl(path = null) {
    //get organization path from dashboard/active account
    let myPagesUrl = '/mypages/account';
    const currentAccountUrl = store.getters['dashboard/currentAccountUrl']
    if (currentAccountUrl) {
      myPagesUrl = currentAccountUrl + (path ? '/' + path : '')
    }
    return this.getLocalisedUrl(myPagesUrl)
  },

  getLocalisedOrganizationUrl (organization, path = null) {
    let organizationUrl = null
    if (organization){
      organizationUrl = organization.identifier
    }
    
    let resultUrl = null
    const locale = locales.getLocale()
    if (!organizationUrl){
      resultUrl = locale.base + '/'
    } else {
      resultUrl = locale.base + '/join/' + organizationUrl
    }

    if (path){
      resultUrl += '/' + path
    }
    return resultUrl
  },

  getCurrentOrganizationPath(path) {
    let currentOrganization = store.getters['session/organizationByLocation']
    return this.getLocalisedOrganizationUrl(currentOrganization, path)
  },
  getPathToEvent(organization, classIdentifier, eventId) {
    return this.getLocalisedOrganizationUrl(organization, 'class/' + classIdentifier + '?event=' + eventId)
  },

  /** For a class from the public API, which carries its studio as `organization_lookup`. */
  getLocalisedClassUrl(myClass) {
    return this.getLocalisedClassPageUrl(myClass?.organization_lookup, myClass?.identifier)
  },
  /**
   * Throws instead of falling back: a link to some other page (the site root) looks like a working
   * link and hides the missing data. That is how the trainer's class preview opened the home page.
   */
  getLocalisedClassPageUrl(organization, classIdentifier) {
    if (!organization?.identifier || !classIdentifier) {
      throw new Error(`Cannot build a class page URL: organization=${organization?.identifier}, class=${classIdentifier}`)
    }
    return this.getLocalisedOrganizationUrl(organization, OrganizationPath.CLASS + '/' + classIdentifier)
  },

  getUrlInLocale (lang, url) {
    if (!lang) {
      return null
    }

    const locale = locales.getLocaleByCode(lang)
    if (!url) {
      return locale.base
    }

    if (url.startsWith('/')) {
      url = url.substring(1)
    }

    return locale.base + '/' + url
  },

  getUrlWithoutLocale () {
    return this.stripLocaleFromPath((window.location.pathname ?? '') + (window.location.search ?? '') + (window.location.hash ?? ''))
  },

  isHomePage() {
    return this.getUrlWithoutLocale() === '/' || this.getUrlWithoutLocale() === ''
  },

  stripLocaleFromPath(path){
    let tmpPath = path
    // Use the consolidated locales configuration instead of hardcoded regex
    const localeCodes = locales.getAllLanguageCodes()
    const localeRegex = new RegExp(`^\\/(${localeCodes.join('|')})($|\\/|\\?|#)`)
    if (localeRegex.test(tmpPath)) {
      tmpPath = tmpPath.substring(3)
    }
    return tmpPath
  },

  getLangFromUrl () {
    var locale = null
    var url = window.location.pathname
    // Use the consolidated locales configuration instead of hardcoded regex
    const localeCodes = locales.getAllLanguageCodes()
    const localeRegex = new RegExp(`^\\/(${localeCodes.join('|')})($|\\/|#)`)
    if (localeRegex.test(url)) {
      locale = url.substring(1, 3)
    }
    return locale
  },
  
  isOrganizationPath (path) {
    let tmpPath = this.stripLocaleFromPath(path)
    return tmpPath.startsWith('/join/') ? true : false
  },
  getOrganizationIdentifier (path) {
    if (path && path.includes('/join/')){
      let joinUrlPartLength = 6
      path = path.substring(path.indexOf('/join/') + joinUrlPartLength);
      return path.split('/')[0]
    } else {
      return null
    }
  },

  removeParameters(url){
    if (!url){
      return null
    }
    
    let indexOfQuestionMark = url.indexOf('?')
    if (indexOfQuestionMark > 0){
      url = url.substring(0, indexOfQuestionMark)
    }
    return url
  },

  isValidSocialMediaUrl(url) {
    try {
      //We accept also url without protocol, e.g. "instagram.com/username"
      if (!url.startsWith('http')) {
        url = 'https://' + url
      }
      new URL(url)
      return true
    } catch (error) {
      return false
    }
  },

  formatUrl(url) {
    if (!url) return ''
    
    url = url.trim()
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    if (url.startsWith('www.')) {
      return 'http://' + url
    }
    if (url.startsWith('/')) {
      return this.getFullUrl(url)
    }
    
    return 'http://' + url
  },

  nameToUrl(name) {
    if (!name) {
      return ''
    }
    name = name.trim().toLowerCase().replace(/ /g, '-')
    name = name.replace(/[^a-z0-9-]/g, '')
    name = name.replace(/-+/g, '-')
    name = name.replace(/^-+|-+$/g, '')
    return name
  },

  removeLastSlash(path) {
    if (path.endsWith('/')) {
      return path.substring(0, path.length - 1)
    }
    return path
  },
}